import { AlarmRow, toDisplayRow } from '@/components/alarms/primitives';
import { conditionKeyLabel } from '@/components/alarms/site/condition-key';
import type { ActiveAlarm } from '@/types/alarm.types';
import { useDeviceConditions } from '../../hooks/useDeviceConditions';
import { computeFreshnessTier } from '../../utils/deviceState';
import type { DtcEntry } from '../../utils/dtc';
import { DtcRow } from './DtcRow';

interface Props {
  siteId?: string;
  deviceId?: string;
  /** Live node data from the SLD telemetry overlay. */
  data: Record<string, unknown>;
  showDtc?: boolean;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '--'
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Time-only formatting (`fmtTime`) is fine for section stamps — those reflect
 * a just-completed fetch and are always recent. The dark banner is different:
 * the stale threshold is 60s (djin-cloud#96), so "Not reporting since" can be
 * days or weeks old, and a bare "12:00:00 AM" reads as "today at midnight".
 * Qualify with a date whenever the value isn't from today so the operator
 * can't mistake a week-old outage for a fresh one.
 */
function fmtBannerTime(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';

  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (isToday) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function SectionHeader({ title, count, stamp, testId, qualifier, qualifierTestId }: {
  title: string; count: number; stamp: string; testId?: string;
  /** Optional context rendered next to the title, e.g. the DTC engine model. */
  qualifier?: string;
  qualifierTestId?: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <span className="text-xs uppercase tracking-wider text-ems-text-secondary">{title}</span>
      {qualifier && (
        <span
          data-testid={qualifierTestId}
          className="text-[10px] font-mono text-ems-text-muted"
        >
          · {qualifier}
        </span>
      )}
      <span data-testid={testId} className="text-[10px] font-mono text-ems-text-muted">
        {count}
      </span>
      <span className="ml-auto text-[9px] font-mono text-ems-text-muted opacity-70">{stamp}</span>
    </div>
  );
}

/**
 * The engine model for the DTC section header (ADR-049 Q4, djin-cloud#656).
 *
 * `engineModel` is carried PER ENTRY (it rides `metadata.engine_model` on each
 * `gen.dtc.code` row), not on the parse result, so the header has to derive a
 * single value from the set. Every entry in one section belongs to one device
 * and therefore one engine, so agreement is the expected case.
 *
 * Returns undefined unless the present values agree on exactly one model.
 * Disagreement means the premise "one section, one engine" is false, and
 * picking `entries[0]` would silently label every code on the card with one of
 * two engines — a wrong-but-plausible answer of exactly the kind
 * `alarm-transition-status.ts` refuses to produce. A technician reading
 * `SPN 731` against the wrong model is worse off than one reading it against
 * none. Absent is honest; arbitrary is not.
 */
export function dtcEngineModel(entries: readonly DtcEntry[]): string | undefined {
  const models = new Set<string>();
  for (const e of entries) {
    if (e.engineModel) models.add(e.engineModel);
  }
  return models.size === 1 ? [...models][0] : undefined;
}

function AlarmSection({ title, rows, stamp, nodeDeviceId }: {
  title: string; rows: ActiveAlarm[]; stamp: string; nodeDeviceId?: string;
}) {
  return (
    <div>
      <SectionHeader title={title} count={rows.length} stamp={stamp} />
      {rows.length === 0 ? (
        <span className="text-[11px] italic text-ems-text-muted">None active</span>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <AlarmRow
              key={`${r.device_id}:${r.condition_key}`}
              alarm={toDisplayRow(r)}
              variant="compact"
              subLabel={buildSubLabel(r, nodeDeviceId)}
              // This panel is a detail view with vertical room to grow, unlike
              // the dense Alarms tab / portfolio hub lists AlarmRow also
              // serves — wrap the full description instead of truncating it.
              wrapDescription
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Ack annotations are composed into subLabel rather than added as AlarmRow
 * props — AlarmRow is shared with the Alarms tab and the portfolio hub, and
 * SLD-specific adornments would change those surfaces too. Design doc §8.
 *
 * `nodeDeviceId` is the SLD node's `data.deviceId` — the telemetry join key,
 * which for a block-keyed node (e.g. a BESS tile keyed by its block id
 * `ES-E3E0`) is NOT the same as the constituent rows' own `device_id`
 * (`BMS-001`, `PCS-001` — see C2). `AlarmRow`'s `compact` variant hides the
 * equipment column entirely, so without this a constituent row gives no clue
 * which device it came from. Name it whenever it differs from the node key.
 */
function buildSubLabel(r: ActiveAlarm, nodeDeviceId?: string): string | undefined {
  const parts: string[] = [];
  const key = conditionKeyLabel(r.condition_key);
  if (key) parts.push(key);
  if (nodeDeviceId && r.device_id !== nodeDeviceId) parts.push(r.device_id);
  // No `ACK` adornment: since #684 `bucketDeviceConditions` drops acked rows,
  // so an acked row cannot reach this component — every row rendered here is
  // unacknowledged by construction. `awaiting ack` stays: an
  // underlying-cleared-but-unacked row is exactly what this panel must surface.
  if (r.underlying_cleared_at) parts.push('awaiting ack');
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function DeviceConditionSections({
  siteId, deviceId, data, showDtc = false,
}: Props) {
  const c = useDeviceConditions(siteId, deviceId);

  if (!deviceId) return null;

  // C1: darkness must be judged on freshness ALONE, independent of the fault
  // rung — computeDeviceState's fault branch returns 'fault' before ever
  // reaching its own offline/no_comms/unknown checks, so a faulted-and-dark
  // device (a real combination: djin-cloud#622 faults any block with an
  // Urgent+ alarm regardless of comms state) must not slip past
  // `computeDeviceState`'s tier and read as "healthy enough to vouch for".
  // computeFreshnessTier is the fault-independent twin of those same
  // rungs — see its doc comment in deviceState.ts. Also deliberately NOT
  // reading data.deviceState here (C3): the live telemetry overlay refreshes
  // commsOk/lastSeenAt but not that click-time snapshot.
  const freshness = computeFreshnessTier({
    commsOk: data.commsOk as boolean | null | undefined,
    lastSeenAt: data.lastSeenAt as string | null | undefined,
  });
  const dark = freshness !== 'fresh';
  const stamp = fmtTime(new Date(c.dataUpdatedAt || Date.now()).toISOString());

  if (c.isLoading) {
    return (
      <div data-testid="conditions-loading" className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 rounded-lg bg-ems-bg animate-pulse" />
        ))}
      </div>
    );
  }

  if (c.isError) {
    return (
      <div className="rounded-lg bg-ems-bg px-3 py-2 text-[11px] text-ems-text-muted">
        Unable to load conditions
      </div>
    );
  }

  const dtcCount = c.dtc.entries.length;
  const countMismatch =
    c.dtc.declaredCount !== null && c.dtc.declaredCount !== dtcCount;

  return (
    <div className="space-y-3">
      {dark && (
        <div
          data-testid="device-dark-banner"
          role="status"
          className="rounded-lg border border-ems-amber/30 bg-ems-amber/10 px-2.5 py-1.5 text-[10px] leading-snug text-ems-amber"
        >
          {freshness === 'unknown'
            ? 'No telemetry received for this device.'
            : `Not reporting since ${fmtBannerTime((data.lastSeenAt as string) ?? null)} — conditions may be out of date.`}
        </div>
      )}

      {/* Dimming applies to the sections, not the banner above — the banner
          is the one element meant to catch the operator's eye when a device
          goes dark, so it must render at full opacity. */}
      <div className={`space-y-3 ${dark ? 'opacity-70' : ''}`}>
        <AlarmSection title="Alarms" rows={c.alarms} stamp={stamp} nodeDeviceId={deviceId} />
        <AlarmSection title="Warnings" rows={c.warnings} stamp={stamp} nodeDeviceId={deviceId} />

        {showDtc && (
          <div>
            {/* DTC stamp comes from the row's last_ts, NOT query freshness —
                the code list can be stale while the query is fresh (design doc
                §7.5), AND it must use the date-qualifying formatter (I4): this
                is the one value designed to go stale (upstream emits no event
                when the code set changes under an already-active row,
                Demsv1.0#2156), so a four-day-old list must not read as a bare
                time that looks like "this morning". */}
            <SectionHeader
              title="Active DTC"
              count={dtcCount}
              stamp={c.dtcAsOf ? `codes as of ${fmtBannerTime(c.dtcAsOf)}` : '--'}
              testId="dtc-count"
              qualifier={dtcEngineModel(c.dtc.entries)}
              qualifierTestId="dtc-engine-model"
            />
            {dtcCount === 0 && !c.dtc.rawFallback && (
              <span className="text-[11px] italic text-ems-text-muted">None active</span>
            )}
            {dtcCount > 0 && (
              <div className="space-y-1">
                {c.dtc.entries.map((e, i) => (
                  <DtcRow key={`${e.spn}-${e.fmi}-${i}`} entry={e} />
                ))}
              </div>
            )}
            {countMismatch && (
              <span className="mt-1 block text-[10px] italic text-ems-text-muted">
                {dtcCount} of {c.dtc.declaredCount} codes shown
              </span>
            )}
            {c.dtc.rawFallback && dtcCount === 0 && (
              <span className="font-mono text-[10px] text-ems-text-muted break-all">
                {c.dtc.rawFallback}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
